import React from 'react'
import styled from 'styled-components'
import DatePicker from 'react-datepicker'
import { getPrimaryColor } from 'customer/style/utils'
import { useConfig } from 'customer/routes'
import { useWindowSize } from 'customer/hooks/useWindowSize'
import { useJpHolidays } from 'customer/hooks/useJpHolidays'
import { addDays } from 'date-fns'

import { styleTheme } from '../../../../style/theme'

export const CalendarForm = ({
  onChange,
  includeDates,
  selectedDate,
}: {
  onChange: (date: Date) => void
  includeDates: Date[]
  selectedDate: Date | undefined
}) => {
  const { primaryColor } = useConfig() as { primaryColor?: string }
  const { width } = useWindowSize()
  const holidays = useJpHolidays(new Date(), addDays(new Date(), 100))
  const CalendarContainer = ({ className, children }) => (
    <StyledCalendarContainer className={className} primaryColor={primaryColor} width={width}>
      {children}
    </StyledCalendarContainer>
  )
  return (
    <StyledWrapper>
      <div className="wrapper">
        <div className="title">日付から検索</div>
        <DatePicker
          calendarClassName="calendar"
          locale="ja"
          dateFormat={'yyyy/MM/dd'}
          calendarContainer={CalendarContainer}
          includeDates={includeDates}
          dateFormatCalendar="yyyy年 M月"
          onChange={onChange}
          highlightDates={selectedDate ? [selectedDate] : []}
          openToDate={selectedDate}
          disabledKeyboardNavigation
          inline
          holidays={holidays.map((holiday) => {
            return { date: holiday.date.toISOString(), holidayName: holiday.name }
          })}
          dayClassName={(date) =>
            selectedDate && date.toDateString() === selectedDate.toDateString() ? 'selected_day' : 'non_selected_day'
          }
        />
      </div>
    </StyledWrapper>
  )
}

const StyledWrapper = styled.div`
  display: grid;
  padding: 24px 12px;
  background-color: ${styleTheme.colors.gray.light};
  justify-content: center;
  > .wrapper {
    display: flex;
    gap: 54px;
    flex-wrap: wrap;
    > .title {
      color: ${styleTheme.colors.black};
      padding-top: 14px;
      line-height: 1.5;
    }
    @media screen and (max-width: 767px) {
      gap: 12px;
      justify-content: start;
      > .title {
        padding-top: 0;
      }
    }
  }
`

const StyledCalendarContainer = styled.div<{ primaryColor?: string; width: number }>`
  display: grid;
  border-radius: 6px;
  border-color: #d9d9d9;
  padding: 12px 80px;
  @media screen and (max-width: 767px) {
    padding: 12px;
    width: 100%;
  }

  .react-datepicker__day-name {
    width: 36px;
    font-size: 12px;
    :nth-child(1) {
      color: #e56274;
    }
    :nth-child(7) {
      color: #1e78ff;
    }
  }

  .react-datepicker__current-month {
    font-size: 16px;
  }

  .react-datepicker {
    &__navigation--previous {
      transform: translate(63px, 16px);
    }

    &__navigation--next {
      transform: translate(-63px, 16px);
    }

    &__month-container {
      display: grid;
      width: 100%;
    }

    &__month {
      margin: 0;
    }

    &__day {
      margin: 5px;
      border-radius: 25px;
      font-size: 16px;
      font-weight: 700;
      width: 36px;
      height: 36px;
      line-height: 36px;
      vertical-align: middle;
      padding-left: 1px;
      &[aria-disabled='false'] {
        background: #eeeeee;
      }

      color: ${styleTheme.colors.black};

      &[aria-label$='土曜日'] {
        color: #1e78ff;
      }

      &[aria-label$='日曜日'] {
        color: #e56274;
      }

      &--holidays {
        color: #e56274;
        background-color: transparent;
      }

      &[aria-disabled='true'][aria-label$='土曜日'],
      &[aria-disabled='true'][aria-label$='日曜日'] {
        color: #bfbfbf;
      }

      &--outside-month {
        visibility: hidden;
      }

      &--keyboard-selected,
      &--selected {
        color: #fff !important;
        background-color: ${getPrimaryColor};
      }

      &--disabled {
        color: #bfbfbf;
      }

      @media (hover: hover) {
        &[aria-disabled='false']:hover {
          color: #fff;
          background-color: ${getPrimaryColor};
        }
      }

      @media (hover: none) {
        &[aria-disabled='false']:active {
          color: #fff;
          background-color: ${getPrimaryColor};
        }
      }

      &--highlighted {
        color: #fff;
        background-color: ${getPrimaryColor} !important;

        &[aria-label$='土曜日'] {
          color: #fff;
        }

        &[aria-label$='日曜日'] {
          color: #fff;
        }

        &--holidays {
          color: #fff;
        }

        .selected_day {
          color: green;
          font-size: 36px;
        }
      }
    }

    &__time-container {
      width: auto;
      border-top: 1px solid #eee;
      border-left: none;
      padding-top: 12px;
      margin-left: 0;

      @media screen and (min-width: 768px) {
        padding-left: 24px;
        border-top: none;
        border-left: 1px solid #eee;
        margin-left: 24px;
      }
    }

    &__time-box {
      width: 280px !important;
    }

    &__time-list {
      display: flex;
      flex-wrap: wrap;

      &-item {
        padding: 0 !important;
        border: 1px solid ${getPrimaryColor};
        color: ${getPrimaryColor};
        width: 64px;
        line-height: 30px;
        border-radius: 4px;

        :hover {
          background-color: ${getPrimaryColor} !important;
          color: #fff;
        }
      }
    }

    &__header {
      background-color: #fff;
      border-bottom: none;

      &--time {
        padding-bottom: 20px;
      }
    }

    &__current-month {
      margin-bottom: 12px;
    }

    &-time__header {
      color: ${styleTheme.colors.black};
      text-align: left;
      font-weight: normal;
      font-size: 12px;
      overflow: visible;
    }

    &__current-month {
      color: ${styleTheme.colors.black};
    }

    &__day-name {
      margin: 5px;
      color: ${styleTheme.colors.black};
    }
  }
`
